import React, { JSX, useEffect, useState } from 'react';
import {
	Alert,
	Badge,
	Box,
	Button,
	CloseButton,
	Container,
	Heading,
	HStack,
	SimpleGrid,
	Text,
	VStack,
	Center,
} from '@chakra-ui/react';
import { APIS, mapRoute, ROUTES } from 'services/shared/routerService';
import { toaster } from 'components/toaster';
import { useRouter } from 'next/router';
import { useSession } from 'components/useSession';
import { getCreditPackets } from 'services/shared/creditService';
import { Check } from 'lucide-react';
import { useIAP } from 'hooks/useIAP';
import { getPlatform } from 'services/shared/clientService';
import ScrollReveal from 'components/ScrollReveal';

interface PropsInterface {
	children?: React.ReactNode;
}

function PricingSection(props: PropsInterface): JSX.Element {
	const router = useRouter();
	const [checkoutPriceId, setCheckoutPriceId] = useState<string>('');

	const { success, canceled, priceId, session_id } = router.query as {
		[key: string]: string | null | undefined;
	};

	//@ts-ignore
	const { data: session, status, update: updateSession }: sessionInterface = useSession();
	const { products, isOwned, makePurchase } = useIAP(session?.user?.id ? Number(session.user.id) : undefined);
	const creditPackets = getCreditPackets();

	useEffect(() => {
		if (success === 'true' && !!priceId && !!session_id) {
			setCheckoutPriceId(priceId);
			const packet = creditPackets.find((packet) => packet.priceId === priceId);
			if (packet) {
				// @ts-ignore
				gtag('event', 'conversion', {
					send_to: 'AW-849792296/THZ7CI6Rm50bEKiam5UD',
					transaction_id: session_id,
				});

				toaster.create({
					duration: 10000,
					title: `${packet.name}: Payment successful!`,
					description: `${packet.credits} Credits have been added to your account.`,
					type: 'success',
					closable: true,
				});
			}

			setTimeout(() => {
				if (session) {
					(async () => {
						await updateSession({ id: session.user.id });
					})();
				}
			}, 1000);
		}

		if (canceled === 'true' && !!priceId) {
			setCheckoutPriceId(priceId);
			const packet = creditPackets.find((packet) => packet.priceId === priceId);
			if (packet) {
				toaster.create({
					duration: 10000,
					title: `${packet.name}`,
					description: 'Your payment checkout was canceled. Please try again.',
					type: 'error',
					closable: true,
				});
			}
		}
	}, [success, canceled, priceId]);

	const activePacket = creditPackets.find((packet) => packet.priceId === checkoutPriceId);

	return (
		<Box as="section" py="16">
			<Container maxW="8xl">
				{success === 'true' && priceId && activePacket && (
					<ScrollReveal direction="down">
						<Alert.Root status="success" mb="10" variant="subtle" rounded="xl">
							<Alert.Indicator />
							<Alert.Content>
								<Alert.Title>{activePacket.name}: Payment successful!</Alert.Title>
								<Alert.Description>
									<HStack spaceX="4" mt="2">
										<Text>{activePacket.credits} Credits added to your account.</Text>
										{!!session && (
											<Button
												size="xs"
												variant="outline"
												onClick={() => updateSession({ id: session.user.id })}
											>
												Refresh
											</Button>
										)}
									</HStack>
								</Alert.Description>
							</Alert.Content>
							<CloseButton size="sm" onClick={() => setCheckoutPriceId('')} />
						</Alert.Root>
					</ScrollReveal>
				)}

				<Box m={'0 auto'}>
					<SimpleGrid columns={{ base: 1, sm: 2, lg: session ? 3 : 4 }} gap="5">
						{creditPackets.map((packet, i) => {
							const isPopular = packet.id === 3;
							const isBestValue = packet.id === 4;
							const isFree = packet.id === 1;

							if (session && packet.id === 1) return null;

							return (
								<ScrollReveal key={packet.id} delay={0.1 * i} direction="up">
									<Box
										position="relative"
										bg="bg.panel"
										borderRadius={28}
										p="5"
										pt="10"
										// rounded="xl"
										// borderWidth={isPopular || isBestValue ? '2px' : '1px'}
										// borderColor={
										// 	isPopular ? 'primary.solid' : isBestValue ? 'blue.500' : 'border.default'
										// }
										shadow="sm"
										transition="all 0.3s"
										_hover={{ shadow: 'md', borderColor: 'primary.muted' }}
										display="flex"
										flexDirection="column"
										h="full"
										minH="420px"
									>
										{' '}
										{(isPopular || isBestValue) && (
											<Badge
												position="absolute"
												top="-3"
												left="50%"
												transform="translateX(-50%)"
												variant="solid"
												colorPalette={isPopular ? 'blue' : 'cyan'}
												rounded="full"
												px="3"
												size="sm"
												fontWeight="bold"
											>
												{isPopular ? 'Most Popular' : 'Best Value'}
											</Badge>
										)}
										<Heading size="md" fontWeight="bold">
											{packet.name}
										</Heading>
										<Text fontSize="xs" color="fg.muted" mt="1" minH="32px">
											{packet.description}
										</Text>
										<HStack mt="3" mb="3" spaceX="2" align="baseline">
											<Text fontSize="3xl" fontWeight="extrabold" color="accent">
												{packet.price}
											</Text>
											{!isFree && (
												<VStack align="start" gap={0}>
													<Text
														fontSize="xl"
														color="fg.muted"
														textDecoration="line-through"
														fontWeight="medium"
													>
														{packet.id === 2
															? '$12.50'
															: packet.id === 3
															? '$50.00'
															: '$125.00'}
													</Text>
												</VStack>
											)}
										</HStack>
										<Center bg={'bg.muted'} py="2" rounded="lg" mb="4">
											<HStack>
												<Text fontSize="xl" fontWeight="bold" color={'fg.default'}>
													{packet.credits}
												</Text>
												<Text fontSize="xs" color="fg.muted" mt={1}>
													credits
												</Text>
											</HStack>
										</Center>
										<VStack spaceY="2" align="start" flex="1">
											<Text fontSize="xs" fontWeight="bold" color="fg.muted">
												Generate up to
											</Text>

											{packet.images > 0 && (
												<HStack spaceX="2">
													<Check size={14} color="var(--chakra-colors-green-500)" />
													<Text fontSize="xs">
														<strong>{packet.images}</strong> HD images
													</Text>
												</HStack>
											)}

											{packet.videos.amount > 0 && (
												<HStack spaceX="2">
													<Check size={14} color="var(--chakra-colors-green-500)" />
													<Text fontSize="xs">
														<strong>{packet.videos.amount}</strong> HD{' '}
														{packet.videos.amount === 1 ? 'video' : 'videos'} (~
														{packet.videos.minutes} min)
													</Text>
												</HStack>
											)}

											<HStack spaceX="2">
												<Check
													size={14}
													color={
														isFree
															? 'var(--chakra-colors-orange-400)'
															: 'var(--chakra-colors-green-500)'
													}
												/>
												<Text fontSize="xs">{packet.quality}</Text>
											</HStack>

											<HStack spaceX="2">
												<Check
													size={14}
													color={
														packet.watermark
															? 'var(--chakra-colors-orange-400)'
															: 'var(--chakra-colors-green-500)'
													}
												/>
												<Text fontSize="xs">
													{packet.watermark ? 'watermarked content' : 'no watermark'}
												</Text>
											</HStack>
										</VStack>
										<Button
											mt="6"
											width="full"
											size="md"
											rounded="3xl"
											bg={isFree ? 'primary' : 'blackAlpha.900'}
											color={isFree ? 'realWhite' : 'realWhite'}
											_hover={{ opacity: 0.8 }}
											onClick={(e) => {
												if (
													(getPlatform() as any) === 'ios' &&
													!!packet.apple_product_id &&
													session
												) {
													makePurchase(packet.apple_product_id);
												} else {
													e.preventDefault();
													const url =
														isFree || !session
															? ROUTES.REGISTER
															: mapRoute(
																	APIS.USER_CREATE_CHECKOUT,
																	{ priceId: packet.priceId },
																	false,
																	false,
																	true
															  );
													router.push(url);
												}
											}}
										>
											{isFree ? 'Get Started' : 'Purchase'}
										</Button>
									</Box>
								</ScrollReveal>
							);
						})}
					</SimpleGrid>
				</Box>
			</Container>
		</Box>
	);
}

export default PricingSection;
